import React, { useState } from 'react'
import { useLocation, NavLink, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FaTachometerAlt,
  FaWarehouse,
  FaUsers,
  FaTruck,
  FaUserTie,
  FaClipboardList,
  FaFileInvoice,
  FaChevronDown,
  FaSignOutAlt,
  FaBuilding,
} from 'react-icons/fa'
import { useDispatch } from 'react-redux'
import { logoutUser } from '../../store/slices/authSlice'
import '../../assets/scss/components/Sidebar.scss'

const menuSections = [
  {
    label: 'Overview',
    items: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        icon: <FaTachometerAlt />,
        path: '/dashboard',
      },
      {
        key: 'company',
        label: 'Company',
        icon: <FaBuilding />,
        path: '/company/profile',
      },
    ],
  },
  {
    label: 'Fleet & Team',
    items: [
      { key: 'entity', label: 'Entities', icon: <FaWarehouse />, path: '/entity' },
      { key: 'user', label: 'Users', icon: <FaUsers />, path: '/user' },
      { key: 'truck', label: 'Trucks', icon: <FaTruck />, path: '/truck' },
      { key: 'driver', label: 'Drivers', icon: <FaUserTie />, path: '/driver' },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        key: 'transport',
        label: 'Transport',
        icon: <FaClipboardList />,
        children: [
          { label: 'Requests', path: '/transport-request' },
          { label: 'Proposals', path: '/freight-proposal' },
          { label: 'Nominations', path: '/truck-nomination' },
        ],
      },
      { key: 'bilty', label: 'Bilty', icon: <FaFileInvoice />, path: '/bilty' },
    ],
  },
]

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [openMenus, setOpenMenus] = useState({})

  const toggleMenu = (item) => {
    const opening = !openMenus[item.key]
    setOpenMenus(opening ? { [item.key]: true } : {})
    if (opening && item.children?.length) navigate(item.children[0].path)
  }

  const closeAll = () => {
    setOpenMenus({})
    if (onClose) onClose()
  }

  const handleLogout = () => {
    dispatch(logoutUser())
    navigate('/login')
  }

  const renderItem = (item) => {
    const hasChildren = !!item.children
    const childActive = hasChildren && item.children.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'))
    const isExpanded = !!openMenus[item.key] || childActive

    return (
      <li key={item.key} className="sb-item">
        {hasChildren ? (
          <>
            <button
              className={`sb-link ${childActive || isExpanded ? 'sb-link--active' : ''}`}
              onClick={() => toggleMenu(item)}
              title={item.label}
            >
              <span className="sb-icon">{item.icon}</span>
              <span className="sb-label">{item.label}</span>
              <motion.span
                className="sb-chevron"
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.22 }}
              >
                <FaChevronDown />
              </motion.span>
            </button>
            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.ul
                  className="sb-sub"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.22, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  {item.children.map((child, i) => (
                    <li key={i}>
                      <NavLink
                        to={child.path}
                        className={({ isActive }) => `sb-sub-link ${isActive ? 'sb-sub-link--active' : ''}`}
                        onClick={onClose}
                      >
                        <span className="sb-sub-dot" />
                        {child.label}
                      </NavLink>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </>
        ) : (
          <NavLink
            to={item.path}
            className={({ isActive }) => `sb-link ${isActive ? 'sb-link--active' : ''}`}
            onClick={closeAll}
            title={item.label}
          >
            <span className="sb-icon">{item.icon}</span>
            <span className="sb-label">{item.label}</span>
          </NavLink>
        )}
      </li>
    )
  }

  const sidebarClass = `sb ${isOpen ? 'sb--open' : 'sb--closed'}`

  return (
    <aside className={sidebarClass}>
      <div className="sb-logo">
        <div className="sb-logo-icon">
          <FaTruck />
        </div>
        <div className="sb-logo-text">
          <span className="sb-logo-brand">TradeSetu</span>
          <span className="sb-logo-sub">Transport</span>
        </div>
      </div>

      <nav className="sb-nav">
        {menuSections.map(section => (
          <React.Fragment key={section.label}>
            <p className="sb-section-label">{section.label}</p>
            <ul className="sb-list">
              {section.items.map(renderItem)}
            </ul>
          </React.Fragment>
        ))}
      </nav>

      <div className="sb-footer">
        <button className="sb-logout" onClick={handleLogout}>
          <span className="sb-icon"><FaSignOutAlt /></span>
          <span className="sb-label">Logout</span>
        </button>
      </div>
    </aside>
  )
}

export default Sidebar
